import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import type { Card } from '../game/data/cards/types';
import { handRailClearance } from './HandRail';
import { useLayoutMode } from './useLayoutMode';

// Contextual action bar — the second half of the tavern's tap-select
// grammar. Tapping any card selects it; this bar surfaces the verbs for the
// selected zone (Buy / Play / Sell / Move / Info) as real ≥44px buttons.
// Drag remains the fast path for the same verbs; the bar exists so nothing
// is drag-only or hover-only, and so no tap ever commits gold by itself.
//
// Portal to document.body, anchored ABOVE the hand rail band: the hand must
// stay visible and tappable while a selection is active (switching selection
// is the most common next action). Everything inside is marked
// data-selkeep so the tavern's outside-tap dismissal ignores taps here.
//
// z-order (keep in sync when adding portals):
//   sell strip 20 < targeting lift 30/40 < hand rail 45 < ACTION BAR 46
//   < modals 50 < explain popovers 60

const BAR_Z = 46;

export type ActionTone = 'sodium' | 'red' | 'ice' | 'neutral';

export interface ActionDescriptor {
  id: 'buy' | 'play' | 'sell' | 'move' | 'info' | 'dismiss';
  label: string;
  /** Cost/yield readout ('3g', '+1g'). Rendered at 10px minimum. */
  sublabel?: string;
  tone: ActionTone;
  disabled?: boolean;
  /** Shown inline when the primary action is disabled — replaces the old
      error toast, so the "why not" is visible before the player commits. */
  disabledReason?: string;
  onPress: () => void;
}

const TONE_CLASSES: Record<ActionTone, string> = {
  sodium: 'border-sodium/60 bg-sodium/20 text-sodium',
  red: 'border-aerosolRed/60 bg-aerosolRed/15 text-aerosolRed',
  ice: 'border-iceCrystal/60 bg-iceCrystal/10 text-iceCrystal',
  neutral: 'border-stucco3 bg-stucco1/90 text-bone',
};

export interface ActionBarProps {
  open: boolean;
  /** Selected card — name + stats give the bar its context line. */
  card: Card | null;
  /** Mode hint ('tap a slot to move it') shown instead of the card line. */
  hint?: string;
  actions: ActionDescriptor[];
  onDismiss: () => void;
  reducedMotion: boolean;
}

export function ActionBar({
  open,
  card,
  hint,
  actions,
  onDismiss,
  reducedMotion,
}: ActionBarProps) {
  const { mode } = useLayoutMode();
  if (typeof document === 'undefined') return null;

  const disabledPrimary = actions.find((a) => a.disabled && a.disabledReason);

  // Always mounted, animated by the `open` flag — the same pattern as
  // HandRail. AnimatePresence-on-portal left the exited node in the DOM
  // (opacity 0 but pointer-events auto), an invisible tap-blocker over the
  // hand band. With this shape the closed bar is inert by construction.
  return createPortal(
    <motion.div
      data-selkeep
      initial={false}
      animate={
        reducedMotion
          ? { opacity: open ? 1 : 0 }
          : { opacity: open ? 1 : 0, y: open ? 0 : 24 }
      }
      transition={{ duration: reducedMotion ? 0 : 0.16, ease: 'easeOut' }}
      className={`fixed inset-x-0 flex justify-center px-xs ${
        open ? 'pointer-events-auto' : 'pointer-events-none'
      }`}
      style={{
        zIndex: BAR_Z,
        // Sit directly above the hand band so the hand stays reachable.
        bottom: `calc(${handRailClearance(mode)} + env(safe-area-inset-bottom, 0px) + 6px)`,
      }}
      aria-hidden={!open}
      role="toolbar"
      aria-label={card ? `Actions for ${card.name}` : 'Card actions'}
    >
          <div
            className="flex w-full max-w-md flex-col gap-[4px] rounded-md border border-stucco3 bg-asphalt/95 px-sm py-[6px] shadow-lg backdrop-blur-sm"
            style={{ boxShadow: '0 6px 24px rgba(0,0,0,0.55)' }}
          >
            {/* Context line: what's selected / what to do next / why blocked */}
            <div className="flex min-w-0 items-baseline gap-sm">
              {hint ? (
                <span className="truncate font-data text-[11px] uppercase tracking-[0.15em] text-sodium">
                  {hint}
                </span>
              ) : card ? (
                <>
                  <span className="truncate font-data text-[12px] font-bold text-bone">
                    {card.name}
                  </span>
                  <span className="shrink-0 font-data text-[11px] text-graphite">
                    <span className="text-aerosolRed">{card.attack}</span>
                    {'/'}
                    <span className="text-glowStick">{card.health}</span>
                  </span>
                </>
              ) : null}
              {disabledPrimary && !hint && (
                <span className="ml-auto shrink-0 font-data text-[10px] uppercase tracking-[0.12em] text-aerosolRed">
                  {disabledPrimary.disabledReason}
                </span>
              )}
            </div>

            <div className="flex items-stretch gap-xs">
              {actions.map((a) => (
                <button
                  key={a.id}
                  onClick={a.disabled ? undefined : a.onPress}
                  disabled={a.disabled}
                  aria-disabled={a.disabled}
                  className={`flex min-h-[48px] flex-1 flex-col items-center justify-center gap-[1px] rounded-md border font-data uppercase tracking-[0.12em] transition-colors ${
                    TONE_CLASSES[a.tone]
                  } ${a.disabled ? 'opacity-40' : 'active:brightness-125'} ${
                    a.id === 'dismiss' || a.id === 'info' ? 'max-w-[72px]' : ''
                  }`}
                >
                  <span className="text-[12px] font-bold leading-none">{a.label}</span>
                  {a.sublabel && (
                    <span className="text-[10px] leading-none opacity-90">{a.sublabel}</span>
                  )}
                </button>
              ))}
              <button
                onClick={onDismiss}
                aria-label="dismiss"
                className="flex min-h-[48px] w-[48px] shrink-0 items-center justify-center rounded-md border border-stucco3 bg-stucco1/90 font-data text-[14px] text-graphite active:brightness-125"
              >
                ✕
              </button>
            </div>
          </div>
    </motion.div>,
    document.body,
  );
}
